import React, { useState, useCallback, useMemo, useEffect } from "react";
import { Table, Spinner, Form, Card, Row, Col } from "react-bootstrap";
import Layout from "../../layout/Layout";
import { gunlukToplamAnaliz } from "../../../api/uretim/makineDataApi";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

import { Bar } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

const AYLAR = [
  "Ocak",
  "Şubat",
  "Mart",
  "Nisan",
  "Mayıs",
  "Haziran",
  "Temmuz",
  "Ağustos",
  "Eylül",
  "Ekim",
  "Kasım",
  "Aralık",
];

const COLORS = {
  navy: "#17324D",
  darkNavy: "#0B2239",
  blue: "#285A7E",
  lightBlue: "#EDF4F8",
  borderBlue: "#8FA9BF",

  targetBackground: "rgba(148, 163, 184, 0.72)",
  targetBorder: "rgba(100, 116, 139, 1)",

  productionSuccessBackground: "rgba(34, 197, 94, 0.72)",
  productionSuccessBorder: "rgba(22, 163, 74, 1)",

  productionDangerBackground: "rgba(239, 68, 68, 0.72)",
  productionDangerBorder: "rgba(220, 38, 38, 1)",

  performanceLine: "#F59E0B",
  performancePoint: "#B45309",

  successBackground: "#E8F5EC",
  successText: "#16723A",

  warningBackground: "#FFF3CD",
  warningText: "#856404",

  dangerBackground: "#FFE6E6",
  dangerText: "#B42318",
};

const roundNumber = (value) => {
  const number = Number(value);

  if (!Number.isFinite(number)) return 0;

  return Math.round(number);
};

const roundPercentage = (value) => {
  const number = Number(value);

  if (!Number.isFinite(number)) return 0;

  return Number(number.toFixed(1));
};

const formatNum = (num) =>
  roundNumber(num).toLocaleString("tr-TR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

const formatPerc = (num) =>
  `${roundPercentage(num).toLocaleString("tr-TR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  })}%`;

const chartValueLabelsPlugin = {
  id: "chartValueLabels",

  afterDatasetsDraw(chart, args, pluginOptions) {
    if (!pluginOptions?.display) return;

    const { ctx } = chart;

    ctx.save();
    ctx.textAlign = "center";
    ctx.textBaseline = "bottom";

    chart.data.datasets.forEach((dataset, datasetIndex) => {
      const meta = chart.getDatasetMeta(datasetIndex);

      if (meta.hidden) return;

      meta.data.forEach((element, index) => {
        const rawValue = Number(dataset.data[index] || 0);

        if (!Number.isFinite(rawValue)) return;

        const isPercentage = dataset.yAxisID === "y1";

        const displayValue = isPercentage
          ? roundPercentage(rawValue)
          : roundNumber(rawValue);

        const label = isPercentage
          ? formatPerc(displayValue)
          : formatNum(displayValue);

        ctx.font = isPercentage ? "700 11px Arial" : "600 10px Arial";

        ctx.fillStyle = isPercentage ? COLORS.performancePoint : COLORS.navy;

        const yOffset = isPercentage ? 10 : 6;

        ctx.fillText(label, element.x, element.y - yOffset);
      });
    });

    ctx.restore();
  },
};

function ToplamGunlukSureAnaliz() {
  const now = useMemo(() => new Date(), []);

  const [year, setYear] = useState(String(now.getFullYear()));
  const [monthName, setMonthName] = useState(AYLAR[now.getMonth()]);
  const [apiData, setApiData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    if (!year || !monthName) return;

    const monthIndex = AYLAR.indexOf(monthName) + 1;

    if (monthIndex <= 0) return;

    try {
      setLoading(true);

      const res = await gunlukToplamAnaliz(year, monthIndex);

      if (res?.data?.success) {
        setApiData(res.data);
      } else {
        setApiData(null);
      }
    } catch (err) {
      console.error("Veri çekme hatası:", err);
      setApiData(null);
    } finally {
      setLoading(false);
    }
  }, [year, monthName]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const {
    tableRows,
    machineKeys,
    totals,
    grandTotals,
    machineChartData,
    dailyChartData,
  } = useMemo(() => {
    if (!apiData || !apiData.makinaGunBazli || !apiData.makinaGunBazliUretim) {
      return {
        tableRows: [],
        machineKeys: [],
        totals: {
          totalTarget: {},
          totalProduction: {},
        },
        grandTotals: {
          factoryTotalTarget: 0,
          factoryTotalProd: 0,
        },
        machineChartData: null,
        dailyChartData: null,
      };
    }

    const { makinaGunBazli, makinaGunBazliUretim } = apiData;

    const keys = Array.from(
      new Set([
        ...Object.keys(makinaGunBazli || {}),
        ...Object.keys(makinaGunBazliUretim || {}),
      ])
    ).sort((a, b) => Number(a) - Number(b));

    const allDates = new Set();

    keys.forEach((key) => {
      Object.keys(makinaGunBazli[key] || {}).forEach((date) => {
        allDates.add(date);
      });

      Object.keys(makinaGunBazliUretim[key] || {}).forEach((date) => {
        allDates.add(date);
      });
    });

    const sortedDates = Array.from(allDates).sort();

    const totalTarget = {};
    const totalProduction = {};

    keys.forEach((key) => {
      totalTarget[key] = 0;
      totalProduction[key] = 0;
    });

    let factoryTotalTarget = 0;
    let factoryTotalProd = 0;

    const rows = sortedDates.map((date) => {
      const dateParts = String(date).split("-");
      const dayNumber =
        dateParts.length === 3
          ? Number(dateParts[2])
          : new Date(date).getDate();

      const dayStr = `${dayNumber}.${monthName.slice(0, 3)}`;

      const rowData = {
        dateDisplay: dayStr,
        fullDate: date,
        machines: {},
        dailyTotal: {},
      };

      let dailyTargetSum = 0;
      let dailyProdSum = 0;

      keys.forEach((key) => {
        const hedef = Number(makinaGunBazli[key]?.[date] || 0);
        const uretim = Number(makinaGunBazliUretim[key]?.[date] || 0);

        const yuzde = hedef > 0 ? roundPercentage((uretim / hedef) * 100) : 0;

        totalTarget[key] += hedef;
        totalProduction[key] += uretim;

        dailyTargetSum += hedef;
        dailyProdSum += uretim;

        rowData.machines[key] = {
          hedef: roundNumber(hedef),
          uretim: roundNumber(uretim),
          yuzde,
          hasTarget: hedef > 0,
        };
      });

      const dailyPercentage =
        dailyTargetSum > 0
          ? roundPercentage((dailyProdSum / dailyTargetSum) * 100)
          : 0;

      rowData.dailyTotal = {
        hedef: roundNumber(dailyTargetSum),
        uretim: roundNumber(dailyProdSum),
        yuzde: dailyPercentage,
        hasTarget: dailyTargetSum > 0,
      };

      factoryTotalTarget += dailyTargetSum;
      factoryTotalProd += dailyProdSum;

      return rowData;
    });

    keys.forEach((key) => {
      totalTarget[key] = roundNumber(totalTarget[key]);
      totalProduction[key] = roundNumber(totalProduction[key]);
    });

    factoryTotalTarget = roundNumber(factoryTotalTarget);
    factoryTotalProd = roundNumber(factoryTotalProd);

    const machinePerformance = keys.map((key) => {
      const hedef = Number(totalTarget[key] || 0);
      const uretim = Number(totalProduction[key] || 0);

      return hedef > 0 ? roundPercentage((uretim / hedef) * 100) : 0;
    });

    const machineChart = {
      labels: keys.map((key) => `Makine ${key}`),

      datasets: [
        {
          type: "bar",
          label: "Hedef",
          data: keys.map((key) => roundNumber(totalTarget[key])),
          backgroundColor: COLORS.targetBackground,
          borderColor: COLORS.targetBorder,
          borderWidth: 1,
          borderRadius: 6,
          maxBarThickness: 55,
          yAxisID: "y",
          order: 2,
        },
        {
          type: "bar",
          label: "Üretim",
          data: keys.map((key) => roundNumber(totalProduction[key])),
          backgroundColor: keys.map((key) =>
            totalProduction[key] >= totalTarget[key]
              ? COLORS.productionSuccessBackground
              : COLORS.productionDangerBackground
          ),
          borderColor: keys.map((key) =>
            totalProduction[key] >= totalTarget[key]
              ? COLORS.productionSuccessBorder
              : COLORS.productionDangerBorder
          ),
          borderWidth: 1,
          borderRadius: 6,
          maxBarThickness: 55,
          yAxisID: "y",
          order: 2,
        },
        {
          type: "line",
          label: "Performans %",
          data: machinePerformance.map(roundPercentage),
          borderColor: COLORS.performanceLine,
          backgroundColor: COLORS.performanceLine,
          pointBackgroundColor: COLORS.performancePoint,
          pointBorderColor: "#FFFFFF",
          pointBorderWidth: 2,
          pointRadius: 5,
          pointHoverRadius: 7,
          borderWidth: 3,
          tension: 0.25,
          yAxisID: "y1",
          order: 1,
        },
      ],
    };

    const dailyChart = {
      labels: rows.map((row) => row.dateDisplay),

      datasets: [
        {
          type: "bar",
          label: "Günlük Hedef",
          data: rows.map((row) => roundNumber(row.dailyTotal.hedef)),
          backgroundColor: COLORS.targetBackground,
          borderColor: COLORS.targetBorder,
          borderWidth: 1,
          borderRadius: 4,
          maxBarThickness: 32,
          yAxisID: "y",
          order: 2,
        },
        {
          type: "bar",
          label: "Günlük Üretim",
          data: rows.map((row) => roundNumber(row.dailyTotal.uretim)),
          backgroundColor: rows.map((row) =>
            row.dailyTotal.uretim >= row.dailyTotal.hedef
              ? COLORS.productionSuccessBackground
              : COLORS.productionDangerBackground
          ),
          borderColor: rows.map((row) =>
            row.dailyTotal.uretim >= row.dailyTotal.hedef
              ? COLORS.productionSuccessBorder
              : COLORS.productionDangerBorder
          ),
          borderWidth: 1,
          borderRadius: 4,
          maxBarThickness: 32,
          yAxisID: "y",
          order: 2,
        },
        {
          type: "line",
          label: "Günlük Performans %",
          data: rows.map((row) => roundPercentage(row.dailyTotal.yuzde)),
          borderColor: COLORS.performanceLine,
          backgroundColor: COLORS.performanceLine,
          pointBackgroundColor: COLORS.performancePoint,
          pointBorderColor: "#FFFFFF",
          pointBorderWidth: 1,
          pointRadius: 3,
          pointHoverRadius: 6,
          borderWidth: 2,
          tension: 0.25,
          yAxisID: "y1",
          order: 1,
        },
      ],
    };

    return {
      tableRows: rows,
      machineKeys: keys,
      totals: {
        totalTarget,
        totalProduction,
      },
      grandTotals: {
        factoryTotalTarget,
        factoryTotalProd,
      },
      machineChartData: machineChart,
      dailyChartData: dailyChart,
    };
  }, [apiData, monthName]);

  const getCellStyle = (yuzde, hasTarget) => {
    if (!hasTarget) return {};

    if (yuzde < 50) {
      return {
        backgroundColor: COLORS.dangerBackground,
        color: COLORS.dangerText,
        fontWeight: "700",
      };
    }

    if (yuzde < 80) {
      return {
        backgroundColor: COLORS.warningBackground,
        color: COLORS.warningText,
        fontWeight: "700",
      };
    }

    return {
      backgroundColor: COLORS.successBackground,
      color: COLORS.successText,
      fontWeight: "700",
    };
  };

  const getDailyTotalPercentageStyle = (yuzde, hasTarget) => {
    const baseStyle = {
      fontWeight: "700",
      borderRight: "1px solid #D5E0E8",
    };

    if (!hasTarget) {
      return {
        ...baseStyle,
        backgroundColor: COLORS.lightBlue,
        color: COLORS.navy,
      };
    }

    if (yuzde < 50) {
      return {
        ...baseStyle,
        backgroundColor: COLORS.dangerBackground,
        color: COLORS.dangerText,
      };
    }

    if (yuzde < 80) {
      return {
        ...baseStyle,
        backgroundColor: COLORS.warningBackground,
        color: COLORS.warningText,
      };
    }

    return {
      ...baseStyle,
      backgroundColor: COLORS.successBackground,
      color: COLORS.successText,
    };
  };

  const getBottomTotalCellStyle = ({
    factory = false,
    leftBorder = false,
    percentage = null,
  } = {}) => {
    const background = factory ? COLORS.blue : COLORS.navy;

    let textColor = "#FFFFFF";

    if (percentage !== null) {
      if (percentage < 80) {
        textColor = "#FFE08A";
      } else {
        textColor = "#B7F7C8";
      }
    }

    return {
      backgroundColor: background,
      color: textColor,
      fontWeight: "800",
      borderLeft: leftBorder
        ? "3px solid #FFFFFF"
        : `1px solid ${COLORS.borderBlue}`,

      "--bs-table-bg": background,
      "--bs-table-color": textColor,
      "--bs-table-striped-bg": background,
      "--bs-table-striped-color": textColor,
      "--bs-table-hover-bg": background,
      "--bs-table-hover-color": textColor,
    };
  };

  const commonChartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,

      interaction: {
        mode: "index",
        intersect: false,
      },

      plugins: {
        legend: {
          position: "top",
          labels: {
            usePointStyle: true,
            pointStyle: "rectRounded",
            padding: 20,
            font: {
              size: 12,
              weight: "600",
            },
          },
        },

        title: {
          display: false,
        },

        tooltip: {
          backgroundColor: "rgba(11, 34, 57, 0.96)",
          titleColor: "#FFFFFF",
          bodyColor: "#FFFFFF",
          borderColor: "rgba(255,255,255,0.18)",
          borderWidth: 1,
          padding: 12,
          displayColors: true,

          callbacks: {
            label: (context) => {
              const label = context.dataset.label || "";
              const value = Number(context.parsed.y || 0);

              if (context.dataset.yAxisID === "y1") {
                return `${label}: ${formatPerc(roundPercentage(value))}`;
              }

              return `${label}: ${formatNum(roundNumber(value))}`;
            },

            afterBody: (tooltipItems) => {
              const targetItem = tooltipItems.find(
                (item) =>
                  item.dataset.label === "Hedef" ||
                  item.dataset.label === "Günlük Hedef"
              );

              const productionItem = tooltipItems.find(
                (item) =>
                  item.dataset.label === "Üretim" ||
                  item.dataset.label === "Günlük Üretim"
              );

              if (!targetItem || !productionItem) return [];

              const target = roundNumber(targetItem.parsed.y);
              const production = roundNumber(productionItem.parsed.y);
              const difference = production - target;

              return [
                "",
                `Fark: ${difference >= 0 ? "+" : ""}${formatNum(difference)}`,
              ];
            },
          },
        },

        chartValueLabels: {
          display: false,
        },
      },

      scales: {
        x: {
          grid: {
            display: false,
          },

          ticks: {
            color: "#475467",
            maxRotation: 0,
            minRotation: 0,
            autoSkip: false,
            font: {
              size: 11,
              weight: "600",
            },
          },
        },

        y: {
          beginAtZero: true,
          grace: "10%",

          grid: {
            color: "rgba(148, 163, 184, 0.20)",
          },

          ticks: {
            precision: 0,
            maxTicksLimit: 8,
            color: "#475467",

            callback: (value) =>
              roundNumber(value).toLocaleString("tr-TR", {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              }),
          },

          title: {
            display: true,
            text: "Hedef / Üretim",
            color: "#344054",
            font: {
              size: 12,
              weight: "700",
            },
          },
        },

        y1: {
          beginAtZero: true,
          suggestedMax: 110,
          position: "right",

          grid: {
            drawOnChartArea: false,
          },

          ticks: {
            precision: 0,
            stepSize: 10,
            color: COLORS.performancePoint,

            callback: (value) =>
              `${roundNumber(value).toLocaleString("tr-TR", {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              })}%`,
          },

          title: {
            display: true,
            text: "Performans",
            color: COLORS.performancePoint,
            font: {
              size: 12,
              weight: "700",
            },
          },
        },
      },
    }),
    []
  );

  const machineChartOptions = useMemo(
    () => ({
      ...commonChartOptions,

      plugins: {
        ...commonChartOptions.plugins,

        chartValueLabels: {
          display: true,
        },
      },
    }),
    [commonChartOptions]
  );

  const dailyChartOptions = useMemo(
    () => ({
      ...commonChartOptions,

      plugins: {
        ...commonChartOptions.plugins,

        chartValueLabels: {
          display: false,
        },
      },
    }),
    [commonChartOptions]
  );

  return (
    <Layout>
      <div className="container-fluid mt-3">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
          <h4 className="fw-bold mb-0">Günlük Toplam Hedef / Üretim</h4>

          <div className="d-flex gap-2">
            <Form.Select
              value={year}
              onChange={(e) => setYear(e.target.value)}
              size="sm"
              style={{ width: "100px" }}
            >
              <option value="2026">2026</option>
              <option value="2025">2025</option>
            </Form.Select>

            <Form.Select
              value={monthName}
              onChange={(e) => setMonthName(e.target.value)}
              size="sm"
              style={{ width: "120px" }}
            >
              {AYLAR.map((ay) => (
                <option key={ay} value={ay}>
                  {ay}
                </option>
              ))}
            </Form.Select>
          </div>
        </div>

        {loading && (
          <div className="text-center py-5">
            <Spinner animation="border" />
          </div>
        )}

        {!loading && apiData && (
          <>
            <div
              className="table-responsive shadow-sm"
              style={{
                maxHeight: "82vh",
                overflowY: "auto",
                borderRadius: "8px",
                border: "1px solid #D9E2EA",
              }}
            >
              <Table
                bordered
                hover
                size="sm"
                className="text-center align-middle mb-0"
                style={{
                  fontSize: "13px",
                  borderColor: "#DEE2E6",
                }}
              >
                <thead
                  className="sticky-top"
                  style={{
                    top: 0,
                    zIndex: 5,
                  }}
                >
                  <tr>
                    <th
                      rowSpan="2"
                      className="align-middle bg-white"
                      style={{
                        minWidth: "80px",
                        borderBottom: "2px solid #CCC",
                      }}
                    >
                      TARİH
                    </th>

                    {machineKeys.map((key) => (
                      <th
                        key={key}
                        colSpan="3"
                        className="fw-bold bg-light"
                        style={{
                          borderLeft: "2px solid #AAA",
                          borderBottom: "1px solid #CCC",
                        }}
                      >
                        MAKİNA - {key}
                      </th>
                    ))}

                    <th
                      colSpan="3"
                      className="fw-bold"
                      style={{
                        backgroundColor: COLORS.navy,
                        color: "#FFFFFF",
                        borderLeft: `3px solid ${COLORS.darkNavy}`,
                        borderBottom: "1px solid #CCC",

                        "--bs-table-bg": COLORS.navy,
                        "--bs-table-color": "#FFFFFF",
                      }}
                    >
                      TOPLAM / GÜN
                    </th>
                  </tr>

                  <tr>
                    {machineKeys.map((key) => (
                      <React.Fragment key={key}>
                        <th
                          className="text-muted small bg-light"
                          style={{
                            borderLeft: "2px solid #AAA",
                          }}
                        >
                          HEDEF
                        </th>

                        <th className="text-muted small bg-light">ÜRETİM</th>

                        <th className="text-muted small bg-light">%</th>
                      </React.Fragment>
                    ))}

                    <th
                      className="small fw-bold"
                      style={{
                        backgroundColor: COLORS.navy,
                        color: "#FFFFFF",
                        borderLeft: `3px solid ${COLORS.darkNavy}`,

                        "--bs-table-bg": COLORS.navy,
                        "--bs-table-color": "#FFFFFF",
                      }}
                    >
                      HEDEF
                    </th>

                    <th
                      className="small fw-bold"
                      style={{
                        backgroundColor: COLORS.navy,
                        color: "#FFFFFF",

                        "--bs-table-bg": COLORS.navy,
                        "--bs-table-color": "#FFFFFF",
                      }}
                    >
                      ÜRETİM
                    </th>

                    <th
                      className="small fw-bold"
                      style={{
                        backgroundColor: COLORS.navy,
                        color: "#FFFFFF",

                        "--bs-table-bg": COLORS.navy,
                        "--bs-table-color": "#FFFFFF",
                      }}
                    >
                      %
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {tableRows.map((row) => (
                    <tr key={row.fullDate}>
                      <td className="fw-bold text-start ps-3 bg-white">
                        {row.dateDisplay}
                      </td>

                      {machineKeys.map((key) => {
                        const data = row.machines[key];

                        return (
                          <React.Fragment key={key}>
                            <td
                              style={{
                                borderLeft: "2px solid #AAA",
                              }}
                            >
                              {data.hasTarget ? formatNum(data.hedef) : "-"}
                            </td>

                            <td className="fw-medium">
                              {formatNum(data.uretim)}
                            </td>

                            <td
                              style={getCellStyle(data.yuzde, data.hasTarget)}
                            >
                              {data.hasTarget ? formatPerc(data.yuzde) : "-"}
                            </td>
                          </React.Fragment>
                        );
                      })}

                      <td
                        style={{
                          borderLeft: `3px solid ${COLORS.darkNavy}`,
                          backgroundColor: COLORS.lightBlue,
                          color: COLORS.navy,
                          fontWeight: "800",

                          "--bs-table-bg": COLORS.lightBlue,
                          "--bs-table-color": COLORS.navy,
                        }}
                      >
                        {row.dailyTotal.hasTarget
                          ? formatNum(row.dailyTotal.hedef)
                          : "-"}
                      </td>

                      <td
                        style={{
                          backgroundColor: COLORS.lightBlue,
                          color: COLORS.navy,
                          fontWeight: "800",

                          "--bs-table-bg": COLORS.lightBlue,
                          "--bs-table-color": COLORS.navy,
                        }}
                      >
                        {formatNum(row.dailyTotal.uretim)}
                      </td>

                      <td
                        style={getDailyTotalPercentageStyle(
                          row.dailyTotal.yuzde,
                          row.dailyTotal.hasTarget
                        )}
                      >
                        {row.dailyTotal.hasTarget
                          ? formatPerc(row.dailyTotal.yuzde)
                          : "-"}
                      </td>
                    </tr>
                  ))}

                  <tr
                    className="sticky-bottom"
                    style={{
                      bottom: 0,
                      zIndex: 4,
                      boxShadow: "0 -3px 10px rgba(0,0,0,0.22)",
                    }}
                  >
                    <td
                      style={getBottomTotalCellStyle({
                        leftBorder: false,
                      })}
                    >
                      TOPLAM
                    </td>

                    {machineKeys.map((key) => {
                      const totalTarget = totals.totalTarget[key] || 0;

                      const totalProduction = totals.totalProduction[key] || 0;

                      const totalPercentage =
                        totalTarget > 0
                          ? roundPercentage(
                              (totalProduction / totalTarget) * 100
                            )
                          : 0;

                      return (
                        <React.Fragment key={key}>
                          <td
                            style={getBottomTotalCellStyle({
                              leftBorder: true,
                            })}
                          >
                            {formatNum(totalTarget)}
                          </td>

                          <td style={getBottomTotalCellStyle()}>
                            {formatNum(totalProduction)}
                          </td>

                          <td
                            style={getBottomTotalCellStyle({
                              percentage: totalPercentage,
                            })}
                          >
                            {formatPerc(totalPercentage)}
                          </td>
                        </React.Fragment>
                      );
                    })}

                    {(() => {
                      const factoryTarget = grandTotals.factoryTotalTarget || 0;

                      const factoryProduction =
                        grandTotals.factoryTotalProd || 0;

                      const factoryPercentage =
                        factoryTarget > 0
                          ? roundPercentage(
                              (factoryProduction / factoryTarget) * 100
                            )
                          : 0;

                      return (
                        <>
                          <td
                            style={getBottomTotalCellStyle({
                              factory: true,
                              leftBorder: true,
                            })}
                          >
                            {formatNum(factoryTarget)}
                          </td>

                          <td
                            style={getBottomTotalCellStyle({
                              factory: true,
                            })}
                          >
                            {formatNum(factoryProduction)}
                          </td>

                          <td
                            style={getBottomTotalCellStyle({
                              factory: true,
                              percentage: factoryPercentage,
                            })}
                          >
                            {formatPerc(factoryPercentage)}
                          </td>
                        </>
                      );
                    })()}
                  </tr>
                </tbody>
              </Table>
            </div>

            <Row className="mt-4 g-4">
              <Col xs={12}>
                <Card className="shadow-sm border-0">
                  <Card.Header
                    className="bg-white py-3"
                    style={{
                      borderBottom: "1px solid #E4E7EC",
                    }}
                  >
                    <div className="fw-bold">Makine Bazlı Aylık Performans</div>

                    <div
                      className="small text-muted mt-1"
                      style={{ fontWeight: 400 }}
                    >
                      Gri sütun hedefi, renkli sütun üretimi, turuncu çizgi ise
                      gerçekleşme yüzdesini gösterir.
                    </div>
                  </Card.Header>

                  <Card.Body>
                    <div style={{ height: "390px" }}>
                      {machineChartData && (
                        <Bar
                          data={machineChartData}
                          options={machineChartOptions}
                          plugins={[chartValueLabelsPlugin]}
                        />
                      )}
                    </div>
                  </Card.Body>
                </Card>
              </Col>

              <Col xs={12}>
                <Card className="shadow-sm border-0">
                  <Card.Header
                    className="bg-white py-3"
                    style={{
                      borderBottom: "1px solid #E4E7EC",
                    }}
                  >
                    <div className="fw-bold">Günlük Toplam Performans</div>

                    <div
                      className="small text-muted mt-1"
                      style={{ fontWeight: 400 }}
                    >
                      Ay içerisindeki günlük toplam hedef ve üretim değerleri
                      karşılaştırılır. Turuncu çizgi günlük performans
                      yüzdesidir.
                    </div>
                  </Card.Header>

                  <Card.Body style={{ overflowX: "auto" }}>
                    <div
                      style={{
                        height: "400px",
                        width: `${Math.max(1200, tableRows.length * 100)}px`,
                      }}
                    >
                      {dailyChartData && (
                        <Bar
                          data={dailyChartData}
                          options={dailyChartOptions}
                          plugins={[chartValueLabelsPlugin]}
                        />
                      )}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </>
        )}

        {!loading && !apiData && (
          <Card className="border-0 shadow-sm">
            <Card.Body className="text-center py-5 text-muted">
              Seçilen dönem için veri bulunamadı.
            </Card.Body>
          </Card>
        )}
      </div>
    </Layout>
  );
}

export default ToplamGunlukSureAnaliz;
